/**
 * NL-024 / NL-027 — registry of the editable client tool routes.
 *
 * Maps a `/tools/<segment>` path segment → the `tool_completions` key the
 * page actually saves under. For all but one the two are identical; the
 * F-022 back-compat alias `/tools/kpi-pre-assessment` renders the same page
 * as `/tools/discovery-call`, which saves under `discovery-call`.
 *
 * Membership = "this is a finalize-once client tool form" (every entry below
 * carries the NL-026 FinalizeWarning). `useToolRouteEffects` consults this to
 * (a) redirect a completed tool to its read-only `/view` (NL-024) and
 * (b) recognise a tool route for coach-edit token handling (NL-027).
 *
 * Deliberately excluded: `compass` + `kpi-correlation` (coach-fill / read-only
 * — no client submit) and `legacy-letter` (sealed-letter flow, never a plain
 * completion). Keep this list in lockstep with the `submitToolCompletion`
 * call sites in `routes/tools/*`.
 */
export const EDITABLE_TOOL_ROUTE_KEYS: Readonly<Record<string, string>> = {
  'discovery-call': 'discovery-call',
  'kpi-pre-assessment': 'discovery-call', // F-022 back-compat alias → same page/key
  'client-commitment': 'client-commitment',
  eulogy: 'eulogy',
  superhero: 'superhero',
  'values-card-sort': 'values-card-sort',
  'scrambled-virtues': 'scrambled-virtues',
  'good-day-fallback-day': 'good-day-fallback-day',
  'chaos-scenario-cards': 'chaos-scenario-cards',
  'identity-snapshot': 'identity-snapshot',
  'virtue-shortlist': 'virtue-shortlist',
  'virtue-affirmations': 'virtue-affirmations',
  'wheel-of-life': 'wheel-of-life',
  'hero-vision': 'hero-vision',
  woop: 'woop',
  'energy-audit': 'energy-audit',
  'masterpiece-day': 'masterpiece-day',
  'fixed-bookend': 'fixed-bookend',
  'six-step-reframing': 'six-step-reframing',
  'virtue-reassessment': 'virtue-reassessment',
  'bid-for-connection': 'bid-for-connection',
  'heroic-work-audit': 'heroic-work-audit',
  'dragon-trigger-log': 'dragon-trigger-log',
  'weekly-review': 'weekly-review',
  'weekly-review-v2': 'weekly-review-v2',
  'celebration-system': 'celebration-system',
  'heros-journey': 'heros-journey',
  'identity-gap-reassessment': 'identity-gap-reassessment',
  'letter-to-future-self': 'letter-to-future-self',
};

export interface EditableToolRoute {
  /** The `/tools/<segment>` path segment that was matched. */
  routeSeg: string;
  /** The `tool_completions` key the page saves under (drives the `/view` URL). */
  completionKey: string;
}

/**
 * If `pathname` is an editable tool route (`/tools/<segment>`, no trailing
 * sub-path), return its `{ routeSeg, completionKey }`; otherwise `null`.
 * The `/tools/:key/view` read-only route has an extra segment so it never
 * matches — which keeps the NL-024 redirect from looping.
 */
export function editableToolRouteFor(pathname: string): EditableToolRoute | null {
  const routeSeg = /^\/tools\/([^/]+)\/?$/.exec(pathname)?.[1];
  if (!routeSeg) return null;
  const completionKey = EDITABLE_TOOL_ROUTE_KEYS[routeSeg];
  return completionKey ? { routeSeg, completionKey } : null;
}
